import { AnimatePresence, motion, type PanInfo } from "framer-motion"
import { type ReactNode, useState } from "react"
import DownloadView from "./downloadView"

interface Slide {
    id: string
    labelPrefix: string
    viewComponent: ReactNode
    isAvailable: boolean
}

const SWIPE_OFFSET_THRESHOLD = 50
const SWIPE_VELOCITY_THRESHOLD = 500

const DoduThemeIndex = () => {

    const slides: Slide[] = [
        {
            id: "spring",
            labelPrefix: "SPRING CAMPUS WITH",
            viewComponent: <DownloadView type="spring" />,
            isAvailable: true
        },
        {
            id: "summer",
            labelPrefix: "SUMMER CAMPUS WITH",
            viewComponent: <DownloadView type="summer" />,
            isAvailable: false
        },
        {
            id: "reveal-autumn",
            labelPrefix: "AUTUMN CAMPUS WITH",
            viewComponent: <DownloadView type="autumn" />,
            isAvailable: false
        },
        {
            id: "reveal-winter",
            labelPrefix: "WINTER CAMPUS WITH",
            viewComponent: <DownloadView type="winter" />,
            isAvailable: false
        },
        {
            id: "reveal-night",
            labelPrefix: "NIGHT CAMPUS WITH",
            viewComponent: <DownloadView type="night" />,
            isAvailable: false
        }
    ]

    const [activeSlideIndex, setActiveSlideIndex] = useState(0)
    const [slideDirection, setSlideDirection] = useState(0)
    const activeSlide = slides[activeSlideIndex]

    const moveToSlide = (targetIndex: number) => {
        if (targetIndex === activeSlideIndex) {
            return
        }

        setSlideDirection(targetIndex > activeSlideIndex ? 1 : -1)
        setActiveSlideIndex(targetIndex)
    }

    const moveBySlide = (direction: number) => {
        setSlideDirection(direction)
        setActiveSlideIndex((currentIndex) => (
            (currentIndex + direction + slides.length) % slides.length
        ))
    }

    const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        if (info.offset.x <= -SWIPE_OFFSET_THRESHOLD || info.velocity.x <= -SWIPE_VELOCITY_THRESHOLD) {
            moveBySlide(1)
            return
        }

        if (info.offset.x >= SWIPE_OFFSET_THRESHOLD || info.velocity.x >= SWIPE_VELOCITY_THRESHOLD) {
            moveBySlide(-1)
        }
    }

    return (
        <div className="h-full overflow-hidden overscroll-none rounded-t-3xl border bg-transparent">
            <div className="grid h-full min-h-0 grid-rows-[1fr_auto] overflow-hidden overscroll-none bg-white">
                <div className="relative min-h-0 overflow-hidden overscroll-none">
                    <AnimatePresence initial={false} custom={slideDirection} mode="popLayout">
                        <motion.div
                            key={activeSlide.id}
                            custom={slideDirection}
                            initial={(direction: number) => ({ x: direction >= 0 ? "100%" : "-100%", opacity: 0 })}
                            animate={{ x: 0, opacity: 1 }}
                            exit={(direction: number) => ({ x: direction >= 0 ? "-100%" : "100%", opacity: 0 })}
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                            drag="x"
                            dragDirectionLock
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.18}
                            onDragEnd={handleDragEnd}
                            className="absolute inset-0 grid cursor-grab touch-none grid-rows-[auto_1fr] px-6 pt-7 text-center active:cursor-grabbing"
                            style={{ touchAction: "none" }}
                        >
                            <p className="text-lg font-semibold tracking-wide text-black md:text-2xl">
                                {activeSlide.labelPrefix}{" "}
                                <span className="text-[#2f6c5c]">DODU</span>
                            </p>

                            <div className="min-h-0 pt-4">
                                {activeSlide.viewComponent}
                            </div>

                            {!activeSlide.isAvailable && (
                                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/80">
                                    <p className="text-2xl font-bold tracking-wide text-white md:text-3xl">
                                        Comming Soon
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="flex items-center justify-center gap-2 py-5">
                    {slides.map((slide, index) => {
                        const isActive = index === activeSlideIndex

                        return (
                            <button
                                key={slide.id}
                                type="button"
                                className={`h-2 rounded-full transition-all duration-300 ${isActive ? "w-5 bg-[#2f6c5c]" : "w-2 bg-black/30"}`}
                                aria-label={`${slide.labelPrefix} DODU slide`}
                                aria-current={isActive}
                                onClick={() => moveToSlide(index)}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default DoduThemeIndex
